import React, { FC, useMemo } from "react";
import { MRT_ColumnDef } from "material-react-table";
import { EMPTY_STR } from "@/constants/string";
import { useTable } from "@/hooks/useTable";
import { Table } from "@/components/elements/Table";
import { useEnum } from "@/api/services/global/enum";
import { CaseMatchStatusOptions } from "@/types/enum";
import { Stack, Typography } from "@mui/material";
import { IconActionStar } from "@/assets/icons";
import { divider } from "@/theme/colors";
import { findLabelByValue } from "@/utils/object";
import { TalentStatusTag } from "@/features/talent/components/talent-common/TalentStatusTag";
import {
  useGetCompanySpecificIndustryList,
  useGetLanguageTestList,
  useGetNationalityList,
  useGetResidenceStatusList,
} from "@/api/services/global/master";
import { truncateWithEllipsis } from "@/utils/string";
import { CaseMatchStatusTag } from "@/features/talent/components/talent-common/CaseMatchStatusTag";
import { dayjs } from "@/utils/dayjs";
import { DATE_FORMAT } from "@/constants/datetime";
import { TListAfterImportExcelDto } from "@/api/services/main/talent/dtos/export-talent.dto";
import { useGetUserListPersonInCharge } from "@/api/services/main/user";

type TTalentAfterImport = {
  rows: TListAfterImportExcelDto[];
  isLoading: boolean;
};
export const TalentAfterImport: FC<TTalentAfterImport> = ({ rows, isLoading }) => {
  const { data: genderOptions, isLoading: isGenderLoading } = useEnum("EGender");
  const { data: recruitmentStatusOptions, isLoading: isRecruitmentLoading } = useEnum("ERecruitmentStatus");
  const { nationalityOptions, isLoading: isNationalityLoading } = useGetNationalityList();
  const { residenceStatusOptions, isLoading: isResidenceStatusLoading } = useGetResidenceStatusList();
  const { languageTestOptions: _languageTestOptions, languageTestLevelOptions: _languageTestLevelOptions, isLoading: isLanguageLoading } = useGetLanguageTestList();
  const { userOptions, isLoading: isUserLoading } = useGetUserListPersonInCharge();
  const { companySpecificIndustryOptions, isLoading: isCompanySpecificLoading } = useGetCompanySpecificIndustryList();

  const isTableLoading =
    isLoading ||
    isGenderLoading ||
    isRecruitmentLoading ||
    isNationalityLoading ||
    isResidenceStatusLoading ||
    isLanguageLoading ||
    isUserLoading ||
    isCompanySpecificLoading;

  const columns = useMemo<MRT_ColumnDef<TListAfterImportExcelDto>[]>(
    () => [
      {
        id: "talentNumber",
        header: "人材管理番号",
        Cell: ({ row }) => {
          return (
            <Stack direction="row" alignItems="center">
              <Typography variant="body2">
                {row.original?.talentNumber ? truncateWithEllipsis(row.original.talentNumber, 20) : EMPTY_STR.TEXT}
              </Typography>
            </Stack>
          );
        },
        accessorKey: "talentNumber",
      },
      {
        id: "fullName",
        accessorKey: "fullName",
        Cell: ({ row }) => {
          return (
            <Stack direction="row" alignItems="center">
              {row.original.isRecommendedTalent && <IconActionStar />}
              <Typography variant="body2">{row.original?.fullName ? truncateWithEllipsis(row.original.fullName, 20) : EMPTY_STR.TEXT}</Typography>
            </Stack>
          );
        },
        header: "名前",
        muiTableHeadCellProps: {
          sx: { maxWidth: 183 },
        },
        muiTableBodyCellProps: {
          sx: { maxWidth: 183 },
        },
      },
      {
        id: "nationalityName",
        accessorKey: "nationality.nationalityName",
        Cell: ({ row }) => {
          const { nationalityId } = row.original;
          if (!nationalityId) return EMPTY_STR.TEXT;
          const value = findLabelByValue(nationalityOptions, nationalityId);
          return (
            <Stack direction="row" alignItems="center">
              <Typography variant="body2">{value}</Typography>
            </Stack>
          );
        },
        header: "国籍",
      },
      {
        id: "gender",
        accessorKey: "gender",
        header: "性別",
        Cell: ({ row }) => findLabelByValue(genderOptions, row.original?.gender) || EMPTY_STR.TEXT,
      },
      {
        id: "recruitmentStatus",
        header: "ステータス",
        accessorKey: "recruitmentStatus",
        Cell: ({ row }) => (
          <TalentStatusTag
            status={row.original?.recruitmentStatus}
            title={findLabelByValue(recruitmentStatusOptions, row.original.recruitmentStatus)}
            variant="bold"
          />
        ),
      },
      {
        id: "caseMatchStatus",
        header: "最新進捗",
        accessorKey: "caseMatchStatus",
        Cell: ({ row }) => {
          return (
            <CaseMatchStatusTag
              status={row.original?.caseMatchStatus ?? -1}
              title={findLabelByValue(CaseMatchStatusOptions, row.original?.caseMatchStatus) || "提案なし"}
              variant="bold"
            />
          );
        },
      },
      {
        id: "residenceStatusId",
        header: "在留資格",
        Cell: ({ row }) => {
          const { residenceStatusId } = row.original;
          if (!residenceStatusId) return EMPTY_STR.TEXT;
          return findLabelByValue(residenceStatusOptions, residenceStatusId);
        },
      },
      {
        id: "languageCertificates",
        header: "日本語能力",
        Cell: ({ row }) => row.original.languageCertificatesLabel ?? EMPTY_STR.TEXT,
      },
      {
        id: "companySpecificIndustry",
        header: "分野",
        Cell: ({ row }) => {
          const { companySpecificIndustryId } = row.original;
          if (!companySpecificIndustryId) return EMPTY_STR.TEXT;

          return findLabelByValue(companySpecificIndustryOptions, companySpecificIndustryId);
        },
        muiTableHeadCellProps: {
          sx: { maxWidth: 120 },
        },
        muiTableBodyCellProps: {
          sx: { maxWidth: 120 },
        },
      },
      {
        id: "personInChargeId",
        header: "担当者",
        accessorKey: "personInCharge.fullName",
        Cell: ({ row }) => {
          const { personInChargeId } = row.original;
          return <Typography variant="body2">{findLabelByValue(userOptions, personInChargeId)}</Typography>;
        },
      },
      {
        id: "createdAt",
        header: "登録日",
        Cell: ({ row }) => dayjs(row.original.createdAt).format(DATE_FORMAT),
      },
    ],
    [genderOptions, recruitmentStatusOptions, isTableLoading],
  );
  const { table } = useTable({
    columns,
    rows,
    muiTableContainerProps: {
      sx: { tableLayout: "fixed" },
    },
    defaultColumn: {
      minSize: 50,
      maxSize: Number.MAX_VALUE,
      size: 1,
    },
  });
  return <Table table={table} isLoading={isTableLoading} borderColor={divider.high} height="calc(100vh - 620px)" />;
};
